import React, { useEffect, useState } from "react";
import { collection, getDocs, updateDoc, doc, deleteDoc } from "firebase/firestore";
import { db } from "./firebaseConfig";
import "./ButtonStyles.css"; // For consistent button styles
import { useNavigate } from "react-router-dom";

const ManageUsers = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState("student"); // default view: students
  const [extraTimeInputs, setExtraTimeInputs] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch users from Firestore
  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const fetchedUsers = querySnapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          email: docSnap.data().email || "N/A",
          role: docSnap.data().role || "N/A",
          username: docSnap.data().username || docSnap.data().displayName || "N/A",
          extraTimePercentage: docSnap.data().extraTimePercentage || 0,
        }));
        setUsers(fetchedUsers);
      } catch (error) {
        console.error("Error fetching users:", error);
        alert("Failed to fetch users. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const handleRoleFilter = (role) => {
    setSelectedRole(role);
  };

  const handleUpdateExtraTime = async (userId, newExtraTime) => {
    try {
      await updateDoc(doc(db, "users", userId), { extraTimePercentage: newExtraTime });
      alert("Extra time updated successfully!");
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, extraTimePercentage: newExtraTime } : u
        )
      );
    } catch (error) {
      console.error("Error updating extra time:", error.message);
      alert("Failed to update extra time. Please try again.");
    }
  };

  const handleRoleChange = async (id, newRole) => {
    try {
      await updateDoc(doc(db, "users", id), { role: newRole });
      alert("User role updated successfully!");
      setUsers((prev) =>
        prev.map((u) => (u.id === id ? { ...u, role: newRole } : u))
      );
    } catch (error) {
      console.error("Error updating role:", error.message);
      alert("Failed to update user role. Please try again.");
    }
  };

  const handleDeleteUser = async (id) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this user?");
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "users", id));
      alert("User deleted successfully!");
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (error) {
      console.error("Error deleting user:", error.message);
      alert("Failed to delete user. Please try again.");
    }
  };

  // -------------------------------
  // Improved Styles
  // -------------------------------
  const containerStyle = {
    backgroundColor: "#f0f7f3", // Changed from background image to a solid color
    minHeight: "100vh",
    padding: "20px",
    fontFamily: "'Segoe UI', Roboto, 'Helvetica Neue', sans-serif",
  };

  const tileStyle = {
    backgroundColor: "rgba(255, 255, 255, 0.95)",
    borderRadius: "16px",
    padding: "30px 40px",
    margin: "30px auto",
    maxWidth: "1200px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
  };

  const headerStyle = {
    position: "relative",
    marginBottom: "30px",
    padding: "15px 0",
    borderBottom: "2px solid #e6f2eb",
    textAlign: "center",
  };

  const titleStyle = {
    margin: 0,
    color: "#1e523d",
    fontSize: "32px",
    fontWeight: "600",
    letterSpacing: "0.5px",
  };

  const filterButtonsContainer = {
    display: "flex",
    justifyContent: "center",
    gap: "15px",
    marginBottom: "30px",
  };

  const getFilterButtonStyle = (role) => {
    const isSelected = selectedRole === role;
    return {
      padding: "10px 22px",
      backgroundColor: isSelected ? "#1e523d" : "#f0f7f3",
      color: isSelected ? "#fff" : "#1e523d",
      border: "none",
      borderRadius: "8px",
      fontWeight: isSelected ? "600" : "500",
      fontSize: "15px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: isSelected ? "0 4px 8px rgba(0, 0, 0, 0.15)" : "none",
    };
  };

  const tableContainerStyle = {
    marginTop: "25px",
    overflowX: "auto",
    borderRadius: "12px",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.08)",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: "0",
    overflow: "hidden",
    borderRadius: "12px",
  };

  const tableHeadStyle = {
    backgroundColor: "#2d6a4f",
    color: "#fff",
  };

  const tableHeadCellStyle = {
    padding: "16px",
    textAlign: "left",
    fontWeight: "600",
    letterSpacing: "0.5px",
    fontSize: "16px",
    borderBottom: "none",
  };

  const tableBodyCellStyle = {
    padding: "14px 16px",
    borderBottom: "1px solid #e6f2eb",
    fontSize: "15px",
    verticalAlign: "middle",
  };

  // Action buttons styles
  const actionButtonsContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
  };

  const baseActionButtonStyle = {
    padding: "8px 14px",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "500",
    fontSize: "14px",
    transition: "all 0.2s",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const adminBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#f7dd8f",
    color: "#876600",
    boxShadow: "0 2px 4px rgba(218, 165, 32, 0.2)",
  };

  const teacherBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#c2dbff",
    color: "#0b5cad",
    boxShadow: "0 2px 4px rgba(66, 133, 244, 0.2)",
  };

  const studentBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#c8e6d5",
    color: "#1e523d",
    boxShadow: "0 2px 4px rgba(45, 106, 79, 0.2)",
  };

  const deleteBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#ffd5d5",
    color: "#b52b27",
    boxShadow: "0 2px 4px rgba(217, 83, 79, 0.2)",
  };

  const extraTimeInputStyle = {
    width: "70px",
    padding: "8px 10px",
    border: "1px solid #ccd9d0",
    borderRadius: "6px",
    fontSize: "14px",
    marginRight: "8px",
    textAlign: "center",
  };

  const updateExtraTimeBtnStyle = {
    ...baseActionButtonStyle,
    backgroundColor: "#ffebcc",
    color: "#b26200",
    boxShadow: "0 2px 4px rgba(255, 152, 0, 0.2)",
  };

  const backButtonContainerStyle = {
    marginTop: "30px",
    textAlign: "center",
  };

  const backButtonStyle = {
    padding: "12px 24px",
    backgroundColor: "#2d6a4f",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "500",
    transition: "all 0.2s",
    boxShadow: "0 4px 8px rgba(45, 106, 79, 0.2)",
  };

  const noUsersStyle = {
    padding: "30px 0",
    fontSize: "16px",
    color: "#666",
    textAlign: "center",
    backgroundColor: "#f9f9f9",
    borderRadius: "8px",
    border: "1px dashed #ddd",
  };

  const loadingStyle = {
    textAlign: "center",
    padding: "40px 0",
    color: "#2d6a4f",
    fontSize: "18px",
  };

  // Filter the users based on selected role
  const filteredUsers = users.filter((u) => u.role === selectedRole);

  return (
    <div style={containerStyle}>
      <div style={tileStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Manage Users</h1>
        </div>

        {/* Filter Buttons */}
        <div style={filterButtonsContainer}>
          <button
            onClick={() => handleRoleFilter("student")}
            style={getFilterButtonStyle("student")}
          >
            Students
          </button>
          <button
            onClick={() => handleRoleFilter("teacher")}
            style={getFilterButtonStyle("teacher")}
          >
            Teachers
          </button>
          <button
            onClick={() => handleRoleFilter("admin")}
            style={getFilterButtonStyle("admin")}
          >
            Administrators
          </button>
        </div>

        {/* Users Table */}
        {isLoading ? (
          <div style={loadingStyle}>Loading users...</div>
        ) : filteredUsers.length > 0 ? (
          <div style={tableContainerStyle}>
            <table style={tableStyle}>
              <thead style={tableHeadStyle}>
                <tr>
                  <th style={tableHeadCellStyle}>Name</th>
                  <th style={tableHeadCellStyle}>Email</th>
                  <th style={tableHeadCellStyle}>Role</th>
                  {selectedRole === "student" && (
                    <th style={tableHeadCellStyle}>Extra Time (%)</th>
                  )}
                  <th style={tableHeadCellStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, idx) => (
                  <tr
                    key={u.id}
                    style={{
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f7fbf9",
                    }}
                  >
                    <td 
                      style={{
                        ...tableBodyCellStyle,
                        cursor: 'pointer',
                        color: '#2d6a4f',
                        textDecoration: 'underline',
                      }}
                      onClick={() => navigate(`/student-profile/${u.id}`)}
                      title="View student profile"
                    >
                      {u.username}
                    </td>
                    <td style={tableBodyCellStyle}>{u.email}</td>
                    <td style={tableBodyCellStyle}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: "50px",
                          fontSize: "13px",
                          fontWeight: "500",
                          backgroundColor:
                            u.role === "admin"
                              ? "#f7dd8f"
                              : u.role === "teacher"
                              ? "#c2dbff"
                              : "#c8e6d5",
                          color:
                            u.role === "admin"
                              ? "#876600"
                              : u.role === "teacher"
                              ? "#0b5cad"
                              : "#1e523d",
                        }}
                      >
                        {u.role.charAt(0).toUpperCase() + u.role.slice(1)}
                      </span>
                    </td>
                    {selectedRole === "student" && (
                      <td style={tableBodyCellStyle}>
                        <input
                          type="number"
                          value={
                            extraTimeInputs[u.id] !== undefined
                              ? extraTimeInputs[u.id]
                              : u.extraTimePercentage
                          }
                          onChange={(e) =>
                            setExtraTimeInputs({
                              ...extraTimeInputs,
                              [u.id]: e.target.value,
                            })
                          }
                          style={extraTimeInputStyle}
                        />
                        {"%"}
                      </td>
                    )}
                    <td style={tableBodyCellStyle}>
                      <div style={actionButtonsContainerStyle}>
                        {(user?.role === "admin" || user?.role === "teacher") && (
                          <>
                            {u.role !== "admin" && (
                              <button
                                onClick={() => handleRoleChange(u.id, "admin")}
                                style={adminBtnStyle}
                              >
                                Promote to Admin
                              </button>
                            )}
                            {u.role !== "teacher" && (
                              <button
                                onClick={() => handleRoleChange(u.id, "teacher")}
                                style={teacherBtnStyle}
                              >
                                Promote to Teacher
                              </button>
                            )}
                            {u.role !== "student" && (
                              <button
                                onClick={() => handleRoleChange(u.id, "student")}
                                style={studentBtnStyle}
                              >
                                Set as Student
                              </button>
                            )}
                          </>
                        )}
                        {user?.role === "admin" && (
                          <button onClick={() => handleDeleteUser(u.id)} style={deleteBtnStyle}>
                            Delete User
                          </button>
                        )}
                        {selectedRole === "student" &&
                          (user?.role === "admin" || user?.role === "teacher") && (
                            <button
                              onClick={() =>
                                handleUpdateExtraTime(
                                  u.id,
                                  Number(
                                    extraTimeInputs[u.id] !== undefined
                                      ? extraTimeInputs[u.id]
                                      : u.extraTimePercentage
                                  )
                                )
                              }
                              style={updateExtraTimeBtnStyle}
                            >
                              Update Time
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={noUsersStyle}>
            No users found for the selected role.
          </div>
        )}

        {/* Back to Dashboard Button */}
        <div style={backButtonContainerStyle}>
          <button onClick={() => navigate("/dashboard")} style={backButtonStyle}>
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageUsers;
